import {
  css, type Interpolation, type SerializedStyles,
} from '@emotion/react';
import styled from '@emotion/styled';
import { type Placement } from '@floating-ui/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCaretDown, faCaretUp,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type ReactNode,
  useId, useMemo, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { OverlayLoaderComponent } from '../../components/overlay/overlayLoader.component';
import { OverlayWrapperComponent } from '../../components/overlay/overlayWrapper.component';
import { useTheme } from '../../themes/theme.hooks';
import { type ThemeProps } from '../../types/themeProps';
import {
  getTextInputIconFontSize, InputSize,
} from '../inputs';
import {
  TooltipComponent, type TooltipPlacement,
} from '../tooltip/tooltip.component';
import { usePointerClickEvent } from '../utils/usePointerClickEvent';
import {
  DEFAULT_DROPDOWN_PLACEMENT, DropdownComponent, DropdownPlacement,
} from './dropdown.component';
import {
  DropdownItemComponent, type DropdownItemComponentProps,
} from './dropdownItem.component';
import {
  DropDownItemSize, getRegularDropdownTriggerSize,
} from './regularDropdown.helpers';

const getDropdownTriggerBorderRadius = (placement: Placement) => {
  switch (placement) {
    case DropdownPlacement.TopEnd:
    case DropdownPlacement.TopStart:
    case DropdownPlacement.Top: {
      return '0 0 4px 4px';
    }
    case DropdownPlacement.BottomEnd:
    case DropdownPlacement.BottomStart:
    case DropdownPlacement.Bottom: {
      return '4px 4px 0 0';
    }
    case DropdownPlacement.Left:
    case DropdownPlacement.LeftStart:
    case DropdownPlacement.LeftEnd: {
      return '0 4px 4px 0';
    }
    case DropdownPlacement.Right:
    case DropdownPlacement.RightStart:
    case DropdownPlacement.RightEnd: {
      return '4px 0 0 4px';
    }
    default: {
      return 4;
    }
  }
};

const DropdownTriggerButton = styled.button<Readonly<{
  disabled?: boolean;
  isOpen: boolean;
  placement: Placement;
  triggerHeight: number;
}>>((
  { theme, triggerHeight, isOpen, disabled, placement }
) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.textColors.primary,
  backgroundColor: isOpen ? theme.backgroundColors.highlight : theme.dropdownColors.triggerBackground,
  height: triggerHeight,
  fontSize: '16px',
  lineHeight: '20px',
  fontWeight: 500,
  textAlign: 'left',
  padding: '6px 8px',
  transition: 'background .1s',
  outline: 'none',
  width: '100%',
  borderRadius: isOpen ? getDropdownTriggerBorderRadius(placement) : 4,
  border: `1px solid ${isOpen ? theme.borderColors.activeItem : theme.borderColors.primary}`,
  opacity: disabled ? 0.7 : 1,

  '&:hover': {
    ...(disabled ? {} : { backgroundColor: theme.backgroundColors.highlight }),
  },
}));

const triggerIconStyle = css({
  fontSize: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: '100%',
  marginLeft: 'auto',
});

const prefixTriggerIconStyle = ({ theme, triggerSize }: ThemeProps<{ triggerSize: InputSize }>) => css({
  color: theme.iconColors.secondary,
  display: 'inline-block',
  fontSize: getTextInputIconFontSize(triggerSize),
  padding: '0 6px 0 4px',
});

const caretDownIconStyle = (isDropdownOpen: boolean) => css(triggerIconStyle, {
  transform: isDropdownOpen ? 'rotate(-180deg)' : undefined,
  transition: 'transform .2s',
});

const dropdownContainerStyle = css({
  display: 'flex',
});

const triggerPlaceholderStyle = (
  { theme, allowWordWrapping }: ThemeProps<{ allowWordWrapping?: boolean }>
) => css(ellipsisStyle(allowWordWrapping), {
  color: theme.textColors.primary,
  display: 'flex',
  opacity: .4,
});

const ellipsisStyle = (allowWordWrapping?: boolean) => css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: allowWordWrapping ? 'initial' : 'nowrap',
});

const selectedOptionTextStyle = css({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  minWidth: 0,
});

const iconStyle = css({
  fontSize: 16,
  marginRight: 3,
});

const getDropdownOptionBorderStyle = (placement: Placement) => {
  switch (placement) {
    case DropdownPlacement.TopEnd:
    case DropdownPlacement.TopStart:
    case DropdownPlacement.Top:
    case DropdownPlacement.Left:
    case DropdownPlacement.LeftStart:
    case DropdownPlacement.LeftEnd:
    case DropdownPlacement.Right:
    case DropdownPlacement.RightStart:
    case DropdownPlacement.RightEnd: {
      return css({
        '&:first-of-type': {
          borderTop: 'none',
        },
      });
    }
    default: {
      return {};
    }
  }
};

export type DropdownOption<T> = {
  style?: Interpolation | SerializedStyles;
  value: T;
} & Pick<DropdownItemComponentProps, 'icon' | 'prefix' | 'suffix' | 'name' | 'isDisabled' | 'tooltipContent' | 'tooltipPlacement'>;

export type RegularDropdownProps<T> = Readonly<{
  className?: string;
  excludeSelectedOption?: boolean;
  value: T | null;
  fallbackName?: string;
  isDisabled?: boolean;
  onChange: (selectedValue: T) => void;
  options: ReadonlyArray<DropdownOption<T>>;
  triggerStyle?: Interpolation;
  triggerPlaceholderStyle?: Interpolation;
  triggerIconStyle?: Interpolation;
  prefixTriggerIconStyle?: Interpolation;
  triggerIcon?: IconProp;
  prefixTriggerIcon?: IconProp;
  dropdownContainerStyle?: Interpolation;
  listStyle?: Interpolation;
  itemStyle?: Interpolation;
  itemCustomStyle?: (item: T) => Interpolation;
  itemIconStyle?: Interpolation;
  placeholder?: string;
  placeholderIcon?: IconProp;
  placeholderIconStyle?: Interpolation;
  inPortal?: boolean;
  allowWordWrappingInSelectedOption?: boolean;
  compareOptions?: (a: T | null, b: T | null) => boolean;
  isLoading?: boolean;
  inputSize?: InputSize;
  itemSize?: DropDownItemSize; // is relative to input size
  dropdownPlacement?: Placement;
  disableAutoHeight?: boolean;
  testid?: string;
  tooltipContent?: ReactNode;
  tooltipPlacement?: TooltipPlacement;
}>;

export const dropdownItemsContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
});

export const RegularDropdownComponent = <T extends unknown>({
  allowWordWrappingInSelectedOption,
  className,
  compareOptions: compareOptionsProp,
  disableAutoHeight,
  dropdownContainerStyle: dropdownContainerStyleProp,
  dropdownPlacement: dropdownPlacementProp,
  excludeSelectedOption,
  fallbackName,
  inPortal,
  inputSize,
  isDisabled,
  isLoading,
  itemCustomStyle,
  itemIconStyle: itemIconStyleProp,
  itemSize: itemSizeProp,
  itemStyle,
  listStyle,
  onChange,
  options,
  placeholder,
  placeholderIcon,
  placeholderIconStyle,
  prefixTriggerIcon,
  prefixTriggerIconStyle: prefixTriggerIconStyleProp,
  testid,
  tooltipContent,
  tooltipPlacement,
  triggerIcon,
  triggerIconStyle: triggerIconStyleProp,
  triggerPlaceholderStyle: triggerPlaceholderStyleProp,
  triggerStyle,
  value,
  ...restProps
}: RegularDropdownProps<T>) => {
  const theme = useTheme();
  const listboxId = useId();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const triggerSize = inputSize ?? InputSize.Medium;
  const triggerHeight = getRegularDropdownTriggerSize(triggerSize);
  const itemSize = itemSizeProp ?? DropDownItemSize.Default;
  const dropdownPlacement = dropdownPlacementProp ?? DEFAULT_DROPDOWN_PLACEMENT;

  const compareOptions = useMemo(() => (
    compareOptionsProp ?? ((a: T | null, b: T | null) => a === b)
  ), [compareOptionsProp]);

  const activeOption = useMemo(() => (
    options.find(item => compareOptions(item.value, value)) ?? null
  ), [compareOptions, value, options]);

  const activeOptionName = activeOption?.name ?? fallbackName;
  const activeOptionIcon = activeOption?.icon ?? null;
  const activeOptionStyle = activeOption?.style ?? null;
  const activeOptionSuffix = activeOption?.suffix ?? null;
  const activeOptionPrefix = activeOption?.prefix ?? null;

  const toggleDropdown = () => {
    setIsDropdownOpen(isOpen => !isOpen);
  };

  const { onClick, onPointerDown, onPointerUp } = usePointerClickEvent(toggleDropdown);

  const closeDropdown = () => setIsDropdownOpen(false);

  const dropdownOptions = useMemo(() => {
    if (excludeSelectedOption) {
      return options.filter(item => item.value !== value);
    }

    return options;
  }, [excludeSelectedOption, options, value]);

  const DropdownTrigger = (
    <TooltipComponent
      tooltipContent={tooltipContent}
      placement={tooltipPlacement}
    >
      <DropdownTriggerButton
        {...restProps}
        role="combobox"
        aria-expanded={isDropdownOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        css={triggerStyle}
        data-testid={testid}
        disabled={isDisabled}
        isOpen={isDropdownOpen}
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        placement={dropdownPlacement}
        triggerHeight={triggerHeight}
      >
        {prefixTriggerIcon && (
          <div css={[prefixTriggerIconStyle({ theme, triggerSize }), prefixTriggerIconStyleProp]}>
            <FontAwesomeIcon icon={prefixTriggerIcon} />
          </div>
        )}
        {activeOptionName ? (
          <span
            css={[
              selectedOptionTextStyle,
              activeOptionStyle,
            ]}
          >
            {!!activeOptionIcon && (
              <FontAwesomeIcon
                icon={activeOptionIcon}
                css={[iconStyle, itemIconStyleProp]}
              />
            )}
            {activeOptionPrefix}<span css={ellipsisStyle(allowWordWrappingInSelectedOption)}>{activeOptionName}</span>{activeOptionSuffix}
          </span>
        ) : (
          <span
            css={[
              triggerPlaceholderStyle({ theme, allowWordWrapping: allowWordWrappingInSelectedOption }),
              triggerPlaceholderStyleProp,
            ]}
          >
            {placeholderIcon && (
              <div css={[iconStyle, placeholderIconStyle]}>
                <FontAwesomeIcon icon={placeholderIcon} />
              </div>
            )}
            {placeholder}
          </span>
        )}
        {triggerIcon ? (
          <div css={[triggerIconStyle, triggerIconStyleProp]}>
            <FontAwesomeIcon icon={isDropdownOpen ? faCaretUp : triggerIcon} />
          </div>
        ) : (
          <div css={[caretDownIconStyle(isDropdownOpen), triggerIconStyleProp]}>
            <FontAwesomeIcon icon={faCaretDown} />
          </div>
        )}
      </DropdownTriggerButton>
    </TooltipComponent>
  );

  return (
    <DropdownComponent
      isOpen={isDropdownOpen}
      onClose={closeDropdown}
      css={[dropdownContainerStyle, dropdownContainerStyleProp]}
      className={className}
      triggerComponent={DropdownTrigger}
      menuStyle={listStyle}
      inPortal={inPortal}
      dropdownPlacement={dropdownPlacementProp}
      disableAutoHeight={disableAutoHeight}
    >
      <div
        role="listbox"
        id={listboxId}
        css={dropdownItemsContainerStyle}
      >
        {isLoading && (
          <OverlayWrapperComponent minHeight={100} >
            <OverlayLoaderComponent
              loaderSize={45}
            />
          </OverlayWrapperComponent>
        )}
        {!isLoading && dropdownOptions.map((item, index) => (
          <DropdownItemComponent
            css={[itemStyle, itemCustomStyle?.(item.value), item.style, getDropdownOptionBorderStyle(dropdownPlacement)]}
            key={index}
            onClick={item.isDisabled ? undefined : () => onChange(item.value)}
            icon={item.icon}
            itemIconStyle={itemIconStyleProp}
            name={item.name}
            size={triggerSize}
            subSize={itemSize}
            prefix={item.prefix}
            suffix={item.suffix}
            isDisabled={item.isDisabled}
            isSelected={activeOption?.value === item.value}
            tooltipPlacement={item.tooltipPlacement}
            tooltipContent={item.tooltipContent}
          />
        ))}
      </div>
    </DropdownComponent>
  );
};
